import { useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";

import { ConfigProvider } from "antd";
import { IntlProvider } from "react-intl";

import {
  FunctionsProvider,
  StorageProvider,
  FirestoreProvider,
  AuthProvider,
  useFirebaseApp,
  AppCheckProvider,
} from "reactfire";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { getFirestore } from "firebase/firestore";
import { getStorage } from "firebase/storage";
import { getFunctions } from "firebase/functions";
import { initializeAppCheck, ReCaptchaV3Provider } from "firebase/app-check";

import { currentUser, logout, deauthorize } from "./redux/user/userActions";
import Router from "./router/Router";

import AppLocale from "./languages";

const APP_CHECK_TOKEN = process.env.REACT_APP_FIREBASE_APP_CHECK_TOKEN;

export default function App() {
  // Redux
  const customise = useSelector((state) => state.customise);
  const dispatch = useDispatch();

  // Lang
  const currentAppLocale = AppLocale[customise.language];

  // Firebase
  const app = useFirebaseApp();
  const auth = getAuth(app);
  const functions = getFunctions(app);
  const storage = getStorage(app);
  const firestore = getFirestore(app);

  // dev only: permite que Auth/Firestore funcionen en localhost detrás de App Check.
  // Registrar el UUID que imprime la consola en Firebase Console → App Check → Debug tokens.
  if (process.env.NODE_ENV === "development") {
    // eslint-disable-next-line no-restricted-globals
    self.FIREBASE_APPCHECK_DEBUG_TOKEN = true;
  }

  // App Check se inicializa UNA sola vez por app. Antes se llamaba en cada
  // render, re-inicializando el proveedor y agravando el throttling de reCAPTCHA.
  const appCheck = useMemo(
    () =>
      initializeAppCheck(app, {
        provider: new ReCaptchaV3Provider(APP_CHECK_TOKEN),
        isTokenAutoRefreshEnabled: true,
      }),
    [app]
  );

  useEffect(() => {
    document.querySelector("html").setAttribute("lang", customise.language);
  }, [customise]);

  useEffect(() => {
    // const auth = getAuth(app);
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      // detaching the listener
      if (user) {
        // Gate: no autorizar sesiones con correo sin verificar.
        // Despachamos SOLO la acción LOGOUT plana (sin signOut de Firebase)
        // para no correr contra la escritura users/{uid} del registro, que
        // corre en el mismo contexto autenticado. register/login hacen su
        // propio signOut al terminar.
        if (!user.emailVerified) {
          dispatch(deauthorize());
          return;
        }

        const {
          uid,
          displayName,
          email,
          emailVerified,
        } = user;

        dispatch(
          currentUser({
            uid,
            displayName,
            email,
            emailVerified,
          })
        );
      } else {
        dispatch(logout());
      }
    });
    return () => unsubscribe(); // unsubscribing from the listener when the component is unmounting.
  }, []);

  return (
    <ConfigProvider
      locale={currentAppLocale.antd}
      direction={customise.direction}
    >
      <IntlProvider
        locale={currentAppLocale.locale}
        messages={currentAppLocale.messages}
      >
        <AppCheckProvider sdk={appCheck}>
          <AuthProvider sdk={auth}>
            <FunctionsProvider sdk={functions}>
              <StorageProvider sdk={storage}>
                <FirestoreProvider sdk={firestore}>
                  <Router />
                </FirestoreProvider>
              </StorageProvider>
            </FunctionsProvider>
          </AuthProvider>
        </AppCheckProvider>
      </IntlProvider>
    </ConfigProvider>
  );
}
